
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LeagueDropdownProps {
  leagues: string[];
  selectedLeague: string | null;
  onLeagueSelect: (league: string) => void;
}

export const LeagueDropdown: React.FC<LeagueDropdownProps> = ({
  leagues,
  selectedLeague,
  onLeagueSelect,
}) => {
  // Fetch leagues data to get the correct order
  const { data: leaguesData } = useQuery({
    queryKey: ['leagues'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Sort leagues based on display_order from leagues table
  const sortedLeagues = React.useMemo(() => {
    if (!leaguesData || !leagues.length) return leagues;

    // Filter leaguesData to only include leagues that are in the provided leagues array
    const relevantLeagueData = leaguesData.filter(league => leagues.includes(league.name));
    
    // Sort the relevant leagues by display_order
    const orderedLeagues = [...relevantLeagueData]
      .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
      .map(league => league.name);
    
    // Add any remaining leagues that aren't in the leagues table
    const remainingLeagues = leagues.filter(league => !orderedLeagues.includes(league));
    
    return [...orderedLeagues, ...remainingLeagues];
  }, [leagues, leaguesData]);

  // Display name function to remove numeric prefix and any leading space
  const displayLeagueName = (league: string) => {
    return league.replace(/^\d+\s*/, '').trim();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full sm:w-[200px] flex items-center justify-between gap-2">
          <span className="flex-1 text-left truncate">
            {selectedLeague ? displayLeagueName(selectedLeague) : 'Select League'}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px] max-h-[250px] overflow-y-auto">
        {sortedLeagues.map((league) => (
          <DropdownMenuItem
            key={league}
            className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
            onClick={() => onLeagueSelect(league)}
          >
            {displayLeagueName(league)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
